// src/pages/MarketMap/CommodityStripsCard.jsx
import React, { useMemo } from "react";
import SectionCard from "components/SectionCard";
import { normalizeValue } from "lib/marketFormat";

export default function CommodityStripsCard({ latestMap, labels = {} }) {
  // Pull normalized latest value by symbol
  function getVal(symbol) {
    const v = latestMap?.[symbol];
    if (!v) return null;
    return normalizeValue(symbol, v.val);
  }

  // Stable explicit labels (overridable via props)
  const rowLabels = {
    ng: labels.ng ?? "Natural Gas (NYMEX)",
    ng_fgtz3: labels.ng_fgtz3 ?? "Natural Gas — FGT Zone 3",
    rbob: labels.rbob ?? "Gasoline RBOB",
    ulsd: labels.ulsd ?? "ULS Diesel (HO)",
  };

  const rows = useMemo(() => ([
    {
      key: "ng",
      label: rowLabels.ng,
      spot: getVal("ng-nymex-active"),
      m6: getVal("ng-nymex-fixed-6m"),
      m12: getVal("ng-nymex-fixed-12m"),
      m18: getVal("ng-nymex-fixed-18m"),
      m24: getVal("ng-nymex-fixed-24m"),
    },
    {
      key: "ng_fgtz3",
      label: rowLabels.ng_fgtz3,
      spot: null, // no separate spot symbol for this fixed strip
      m6: getVal("ng-fgtz3-fixed-6m"),
      m12: getVal("ng-fgtz3-fixed-12m"),
      m18: getVal("ng-fgtz3-fixed-18m"),
      m24: getVal("ng-fgtz3-fixed-24m"),
    },
    {
      key: "rbob",
      label: rowLabels.rbob,
      spot: getVal("rbob-nymex-active"),
      m6: getVal("rbob-nymex-fixed-6m"),
      m12: getVal("rbob-nymex-fixed-12m"),
      m18: getVal("rbob-nymex-fixed-18m"),
      m24: getVal("rbob-nymex-fixed-24m"),
    },
    {
      key: "ulsd",
      label: rowLabels.ulsd,
      spot: getVal("ho-nymex-active"),
      m6: getVal("ho-nymex-fixed-6m"),
      m12: getVal("ho-nymex-fixed-12m"),
      m18: getVal("ho-nymex-fixed-18m"),
      m24: getVal("ho-nymex-fixed-24m"),
    },
  ]), [latestMap, rowLabels.ng, rowLabels.ng_fgtz3, rowLabels.rbob, rowLabels.ulsd]);

  const formatVal = (v) => (v == null || !Number.isFinite(v) ? "—" : `$${v.toFixed(2)}`);

  return (
    <SectionCard title="Fixed Price Strips">
      <div className="text-sm text-muted -mt-2 mb-3">Price for selected strips</div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-muted">
              <th className="py-2 pr-4 font-medium">Commodity</th>
              <th className="py-2 pr-4 font-medium">6M</th>
              <th className="py-2 pr-4 font-medium">12M</th>
              <th className="py-2 pr-4 font-medium">18M</th>
              <th className="py-2 pr-0 font-medium">24M</th>
            </tr>
          </thead>
          <tbody className="text-app">
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-app">
                <td className="py-2 pr-4">{r.label}</td>
                <td className="py-2 pr-4 tabular-nums">{formatVal(r.m6)}</td>
                <td className="py-2 pr-4 tabular-nums">{formatVal(r.m12)}</td>
                <td className="py-2 pr-4 tabular-nums">{formatVal(r.m18)}</td>
                <td className="py-2 pr-0 tabular-nums">{formatVal(r.m24)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}
